import React, { useEffect, useRef, useState } from 'react';
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import { useSearchParams } from "react-router-dom";
import Datepicker from "../../components/Datepicker/Datepicker";
import useDatepicker from "../../hooks/useDatepicker/useDatepicker";
import './goldrate.css';
import RateBox from "../../components/ratebox/RateBox";
import Goldchart from "../../components/goldchart/Goldchart";
import axios from 'axios';
import { Helmet } from "react-helmet";

const Goldrate = () => {
  const container = useRef();
  const { datepicker, handleDateChange } = useDatepicker();
  const [searchParams] = useSearchParams();
  const selectedState = searchParams.get("state") || "Gujarat";
  const [goldRateRaw, setGoldRateRaw] = useState(null);
  const [goldRateChange, setGoldRateChange] = useState(null);

  const formatRate = (rate) => {
    return rate?.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) ?? "Loading...";
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js";
    script.type = "text/javascript";
    script.async = true;
    script.innerHTML = `
      {
        "allow_symbol_change": true,
        "calendar": false,
        "details": false,
        "hide_side_toolbar": true,
        "hide_top_toolbar": false,
        "hide_legend": false,
        "hide_volume": false,
        "hotlist": false,
        "interval": "D",
        "locale": "en",
        "save_image": true,
        "style": "1",
        "symbol": "FX_IDC:XAUINRG",
        "theme": "light",
        "timezone": "Etc/UTC",
        "backgroundColor": "#ffffff",
        "gridColor": "rgba(46, 46, 46, 0.06)",
        "watchlist": [],
        "withdateranges": false,
        "compareSymbols": [],
        "studies": [],
        "width": "100%",
        "height": 610
      }`;
    container.current.appendChild(script);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // https://data-asg.goldprice.org/dbXRates/INR
        // const res = await axios.get("https://api.gold-api.com/price/XAU");
        // const pricePerOunce = parseFloat(res.data.price);
        // const pricePerGram = ((pricePerOunce / 28.3495)) * 85;
        // setGoldRateRaw(pricePerGram); // Store raw price per gram

        const res = await axios.get("https://data-asg.goldprice.org/dbXRates/INR");
        // 1.09 is gst in india 9%
        const pricePerGram = (res.data.items[0].xauPrice / 31.1034) * 1.09;
        setGoldRateRaw(pricePerGram);

        const priceChange24K = (res.data.items[0].chgXau / 31.1034) * 1.09;
        setGoldRateChange(priceChange24K);

        const percentChange = (priceChange24K / (pricePerGram - priceChange24K)) * 100;

      } catch (error) {
        console.error("Error fetching gold rate:", error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);

    return () => clearInterval(interval);
  }, []);

  const data = [
    {
      type: "24K Gold /g",
      rate: formatRate(goldRateRaw),
      difference: goldRateChange !== null ? `${goldRateChange >= 0 ? "+" : "-"} ₹${Math.abs(goldRateChange).toFixed(2)}` : "Loading..."
    },
    {
      type: "22K Gold /g",
      rate: goldRateRaw ? formatRate(goldRateRaw * (22 / 24)) : "Loading...",
      difference: goldRateChange !== null ? `${goldRateChange >= 0 ? "+" : "-"} ₹${Math.abs((goldRateChange * (22 / 24)).toFixed(2))}` : "Loading..."
    },
    {
      type: "18K Gold /g",
      rate: goldRateRaw ? formatRate(goldRateRaw * (18 / 24)) : "Loading...",
      difference: goldRateChange !== null ? `${goldRateChange >= 0 ? "+" : "-"} ₹${Math.abs((goldRateChange * (18 / 24)).toFixed(2))}` : "Loading..."
    },
  ];

  data.forEach(item => {
    if (item.difference.trim().startsWith("+")) {
      item.symbol = "up";
    } else if (item.difference.trim().startsWith("-")) {
      item.symbol = "down";
    } else {
      item.symbol = "neutral";
    }
  });

  return (
    <>
      <Helmet>
        <title>
          Today Gold Rate in {selectedState} | Live 24K, 22K, 18K Prices & Chart
        </title>

        <meta
          name="description"
          content={`Check live gold rate in ${selectedState}. View 24K, 22K, and 18K gold prices per gram today, live chart updates, and historical price data.`}
        />
        <meta
          name="keywords"
          content={`gold rate today, ${selectedState} gold price, 24k gold rate, 22k gold rate, 18k gold rate, live gold price India, gold chart, gold price history`}
        />

        {/* Open Graph */}
        <meta
          property="og:title"
          content={`Today Gold Rate in ${selectedState} | Live Gold Price & Chart`}
        />
        <meta
          property="og:description"
          content={`View today's gold price in ${selectedState} for 24K, 22K, and 18K gold. Live chart, latest updates, and market analysis.`}
        />
        <meta
          property="og:url"
          content={`https://gold-rate-today.com/goldrate?state=${selectedState}`}
        />
        <meta
          property="og:image"
          content="https://gold-rate-today.com/images/gold-banner.jpg"
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Today Gold Rate in ${selectedState} | Gold-Rate-Today.com`}
        />
        <meta
          name="twitter:description"
          content="Get live 24K, 22K & 18K gold prices with updated charts and historical data."
        />
        <meta
          name="twitter:image"
          content="https://gold-rate-today.com/images/gold-banner.jpg"
        />

        <link
          rel="canonical"
          href={`https://gold-rate-today.com/goldrate?state=${selectedState}`}
        />

        {/* Structured Data JSON-LD */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Gold Rate Today in ${selectedState}",
            "url": "https://gold-rate-today.com/goldrate?state=${selectedState}",
            "description": "Check today's live gold rate in ${selectedState}. 24K, 22K, 18K gold prices per gram with live charts and historical data.",
            "publisher": {
              "@type": "Organization",
              "name": "Gold-Rate-Today.com",
              "url": "https://gold-rate-today.com/"
            }
          }
        `}</script>
      </Helmet>

      <section className="rate-page">
        <div className="top-head">
          <h3>Gold Price in {selectedState}</h3>
          <Datepicker datepicker={datepicker} handleDateChange={handleDateChange} />
        </div>
        <CustomSelect />
        <div className="rate-main-box">
          {data?.map((item, index) => {
            const { type, rate, difference, symbol } = item;

            return (
              <RateBox
                key={index}
                type={type}
                rate={rate}
                difference={difference}
                symbol={symbol}
              />
            );
          })}
        </div>
        <div className="intro-content">
          <p>
            <span>Today's gold rate in {selectedState} :</span>{" "}
            24 karat gold is priced at <span>₹{data[0].rate}</span> per gram,{" "}
            22 karat gold at <span>₹{data[1].rate}</span> per gram, and{" "}
            18 karat gold (also referred to as 999 gold) at <span>₹{data[2].rate}</span> per gram.
          </p>
        </div>
        <Goldchart />
        <div className="tradingview-widget-container" ref={container} style={{ padding: "15px" }}>
          <div className="tradingview-widget-container__widget"></div>
        </div>
      </section>
    </>
  );
};

export default Goldrate;
